import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import {
  api,
  ApiError,
  type AuditEntry,
  type ListResponse,
  type Me,
  type TenantProfile,
  type TenantSettings,
} from '../lib/api'
import { AUDIT_LABELS } from '../lib/audit-labels'
import { MasterData } from './MasterData'
import { Banner, Button, Card, Chip, SelectField, Spinner, TextField, useToast } from '../ui'

/** Länder, in denen dieser Dienstplan realistisch betrieben wird. */
const COUNTRIES: [string, string][] = [
  ['DE', 'Deutschland'],
  ['AT', 'Österreich'],
  ['CH', 'Schweiz'],
]

/** Zeitzonen, die in einem deutschen Gastronomiebetrieb vorkommen. */
const TIMEZONES = ['Europe/Berlin', 'Europe/Vienna', 'Europe/Zurich']

const REGIONS: [string, string][] = [
  ['DE-BW', 'Baden-Württemberg'],
  ['DE-BY', 'Bayern'],
  ['DE-BE', 'Berlin'],
  ['DE-BB', 'Brandenburg'],
  ['DE-HB', 'Bremen'],
  ['DE-HH', 'Hamburg'],
  ['DE-HE', 'Hessen'],
  ['DE-MV', 'Mecklenburg-Vorpommern'],
  ['DE-NI', 'Niedersachsen'],
  ['DE-NW', 'Nordrhein-Westfalen'],
  ['DE-RP', 'Rheinland-Pfalz'],
  ['DE-SL', 'Saarland'],
  ['DE-SN', 'Sachsen'],
  ['DE-ST', 'Sachsen-Anhalt'],
  ['DE-SH', 'Schleswig-Holstein'],
  ['DE-TH', 'Thüringen'],
]

export function TenantPage({ me }: { me: Me }) {
  const tenant = me.active_tenant
  const queryClient = useQueryClient()
  const toast = useToast()

  const settings = useQuery({
    queryKey: ['tenant-settings', tenant?.id],
    queryFn: () => api.get<TenantSettings>('/api/tenants/current/settings'),
    enabled: Boolean(tenant),
  })

  const audit = useQuery({
    queryKey: ['tenant-audit', tenant?.id],
    queryFn: () => api.get<ListResponse<AuditEntry>>('/api/tenants/current/audit?limit=50'),
    enabled: Boolean(tenant),
  })

  const profile = useQuery({
    queryKey: ['tenant-profile', tenant?.id],
    queryFn: () => api.get<TenantProfile>('/api/tenants/current'),
    enabled: Boolean(tenant),
  })

  const [draft, setDraft] = useState<Partial<TenantSettings>>({})
  const [profileDraft, setProfileDraft] = useState<Partial<TenantProfile>>({})

  // Nach dem Betriebswechsel gehört der halb ausgefüllte Entwurf zum alten
  // Betrieb. Ihn stehen zu lassen hieße, fremde Daten ins Formular zu erben.
  useEffect(() => setProfileDraft({}), [tenant?.id])

  /** Der aktuell anzuzeigende Wert: Entwurf schlägt Gespeichertes. */
  const field = (key: keyof TenantProfile): string =>
    String(profileDraft[key] ?? profile.data?.[key] ?? '')

  const edit = (key: keyof TenantProfile, value: string) =>
    setProfileDraft((previous) => ({ ...previous, [key]: value }))

  const profileDirty = Object.keys(profileDraft).length > 0

  const saveProfile = useMutation({
    mutationFn: () =>
      api.patch<TenantProfile>(
        '/api/tenants/current',
        // Leerer String heißt „Feld leeren" — als null gesendet, weil der
        // Server zwischen „weggelassen" und „ausdrücklich geleert"
        // unterscheidet. Ein '' würde sonst als Wert gespeichert.
        Object.fromEntries(
          Object.entries(profileDraft).map(([key, value]) => [
            key,
            typeof value === 'string' && value.trim() === '' ? null : value,
          ]),
        ),
      ),
    onSuccess: (updated) => {
      toast('Stammdaten gespeichert.', 'success')
      queryClient.setQueryData(['tenant-profile', tenant?.id], updated)
      setProfileDraft({})
      // Der Name steht im Betriebswechsler und im Kopf — `me` muss neu.
      queryClient.invalidateQueries({ queryKey: ['me'] })
    },
    onError: (error) => toast((error as ApiError).message, 'error'),
  })

  const saveSettings = useMutation({
    mutationFn: () => api.patch<TenantSettings>('/api/tenants/current/settings', draft),
    onSuccess: (updated) => {
      toast('Einstellungen gespeichert.', 'success')
      queryClient.setQueryData(['tenant-settings', tenant?.id], updated)
      setDraft({})
      // Die Primärfarbe steuert das ganze Farbschema — die Ansicht muss neu
      // durch `useAppearance`.
      queryClient.invalidateQueries({ queryKey: ['me'] })
    },
    onError: (error) => toast((error as ApiError).message, 'error'),
  })

  if (!tenant) return <Card title="Kein Betrieb ausgewählt">Wähle oben einen Betrieb.</Card>
  if (tenant.role !== 'owner') {
    return (
      <Card title="Betrieb">
        <Banner tone="info">Die Betriebseinstellungen kann nur der Inhaber ändern.</Banner>
      </Card>
    )
  }
  if (settings.isPending) return <Spinner />

  const current = { ...settings.data!, ...draft }
  const dirty = Object.keys(draft).length > 0

  return (
    <div className="stack">
      <h1>Betrieb</h1>

      <Card title="Stammdaten">
        {profile.isPending ? (
          <Spinner />
        ) : (
          <form
            className="stack"
            onSubmit={(event) => {
              event.preventDefault()
              saveProfile.mutate()
            }}
          >
            <TextField
              label="Betriebsname"
              value={field('name')}
              minLength={2}
              required
              onChange={(event) => edit('name', event.target.value)}
              hint="So heißt der Betrieb im Alltag und über dem Dienstplan."
            />
            <TextField
              label="Firmierung"
              value={field('legal_name')}
              onChange={(event) => edit('legal_name', event.target.value)}
              hint="Vollständig, wie im Handelsregister. Erscheint auf Rechnungen."
            />

            <div className="field-row">
              <TextField
                label="Kontakt-E-Mail"
                type="email"
                value={field('contact_email')}
                onChange={(event) => edit('contact_email', event.target.value)}
              />
              <TextField
                label="Telefon"
                value={field('phone')}
                onChange={(event) => edit('phone', event.target.value)}
              />
            </div>

            <TextField
              label="Straße und Hausnummer"
              value={field('street')}
              onChange={(event) => edit('street', event.target.value)}
            />
            <div className="field-row">
              <TextField
                label="Postleitzahl"
                value={field('postal_code')}
                onChange={(event) => edit('postal_code', event.target.value)}
              />
              <TextField
                label="Ort"
                value={field('city')}
                onChange={(event) => edit('city', event.target.value)}
              />
              <SelectField
                label="Land"
                value={field('country')}
                onChange={(event) => edit('country', event.target.value)}
              >
                {/* Leere Auswahl an erster Stelle, weil sie der Wahrheit
                    entspricht: Ist nichts gespeichert, darf das Feld nicht
                    „Deutschland" anzeigen. Genau das tat es vorher — und
                    speicherte trotzdem nichts, weil ein unberührtes Feld nicht
                    in den Entwurf wandert. */}
                <option value="">— keine Angabe —</option>
                {COUNTRIES.map(([code, label]) => (
                  <option key={code} value={code}>
                    {label}
                  </option>
                ))}
              </SelectField>
            </div>

            <TextField
              label="USt-IdNr."
              value={field('vat_id')}
              onChange={(event) => edit('vat_id', event.target.value)}
              hint="Optional."
            />

            <div className="row row--between">
              <p className="muted" style={{ margin: 0, fontSize: '0.8125rem' }}>
                Kürzel: <code>{tenant.slug}</code> — bleibt bestehen, damit bereits
                erstellte Datenexporte gültig bleiben.
              </p>
              <Button type="submit" busy={saveProfile.isPending} disabled={!profileDirty}>
                Speichern
              </Button>
            </div>
          </form>
        )}
      </Card>

      <Card title="Einstellungen">
        <form
          className="stack"
          onSubmit={(event) => {
            event.preventDefault()
            saveSettings.mutate()
          }}
        >
          <div className="grid-2">
            <SelectField
              label="Zeitzone"
              value={current.timezone}
              onChange={(event) => setDraft({ ...draft, timezone: event.target.value })}
              hint="Alle Zeiten werden hierin angezeigt und berechnet."
            >
              {TIMEZONES.map((zone) => (
                <option key={zone} value={zone}>
                  {zone}
                </option>
              ))}
            </SelectField>

            <SelectField
              label="Feiertage"
              value={current.holiday_region}
              onChange={(event) => setDraft({ ...draft, holiday_region: event.target.value })}
              hint="Maßgeblich für Feiertagszuschläge."
            >
              {REGIONS.map(([code, label]) => (
                <option key={code} value={code}>
                  {label}
                </option>
              ))}
            </SelectField>

            <SelectField
              label="Woche beginnt am"
              value={String(current.week_starts_on)}
              onChange={(event) =>
                setDraft({ ...draft, week_starts_on: Number(event.target.value) })
              }
            >
              <option value="1">Montag</option>
              <option value="7">Sonntag</option>
            </SelectField>

            <div className="field">
              <label className="field__label" htmlFor="primary-color">
                Farbe
              </label>
              <div className="row">
                <input
                  id="primary-color"
                  type="color"
                  value={current.primary_color}
                  onChange={(event) =>
                    setDraft({ ...draft, primary_color: event.target.value })
                  }
                  style={{
                    width: 56,
                    height: 44,
                    border: 'none',
                    borderRadius: 'var(--md-sys-shape-md)',
                    background: 'transparent',
                    cursor: 'pointer',
                  }}
                />
                <code>{current.primary_color}</code>
              </div>
              <span className="field__hint">
                Aus dieser Farbe wird das ganze Schema erzeugt. Der Kontrast wird geprüft und
                notfalls korrigiert.
              </span>
            </div>
          </div>

          <div className="row row--between">
            <span className="muted" style={{ fontSize: '0.8125rem' }}>
              Aufbewahrung: {current.retention_planning_days} Tage Planung,{' '}
              {current.retention_time_days} Tage Zeitdaten. Änderbar nur über die Verwaltung.
            </span>
            <Button type="submit" busy={saveSettings.isPending} disabled={!dirty}>
              Speichern
            </Button>
          </div>
        </form>
      </Card>

      <MasterData tenantId={tenant.id} />

      <Card title="Protokoll" action={<Chip>letzte 50 Einträge</Chip>}>
        {audit.isPending && <Spinner />}
        {audit.data && audit.data.items.length === 0 && (
          <p className="muted">Noch keine Einträge.</p>
        )}
        {audit.data && audit.data.items.length > 0 && (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Zeitpunkt</th>
                  <th>Vorgang</th>
                  <th>Ergebnis</th>
                </tr>
              </thead>
              <tbody>
                {audit.data.items.map((entry) => (
                  <tr key={entry.id}>
                    <td data-label="Zeitpunkt" className="muted">
                      {new Date(entry.created_at).toLocaleString('de-DE', {
                        dateStyle: 'short',
                        timeStyle: 'short',
                      })}
                    </td>
                    <td data-label="Vorgang">{AUDIT_LABELS[entry.action] ?? entry.action}</td>
                    <td data-label="Ergebnis">
                      {entry.result === 'success' ? (
                        <Chip tone="success">erfolgreich</Chip>
                      ) : (
                        <Chip tone="danger">gescheitert</Chip>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
